import { useEffect, useState } from 'react';
import type { Me } from '../lib/api';

/** Audit history + field-level revert + snapshot backups (uses the tracker-sync
 *  /history, /revert, /snapshot(s) endpoints). The full-file backup is Export. */

function jx(url: string, init?: RequestInit) {
  return fetch(url, { credentials: 'include', ...(init || {}) }).then((r) =>
    r.json().then((b) => ({ ok: r.ok, status: r.status, body: b })).catch(() => ({ ok: r.ok, status: r.status, body: {} as any })));
}
function Card({ title, children }: { title: string; children: any }) {
  return <div className="card p-3"><div className="text-sm font-semibold mb-2">{title}</div>{children}</div>;
}
const fmt = (ts: number) => (ts ? new Date(ts).toLocaleString() : '');

function HistoryRevert({ apiBase, canEdit }: { apiBase: string; canEdit: boolean }) {
  const [rows, setRows] = useState<any[]>([]);
  const [q, setQ] = useState('');
  const [msg, setMsg] = useState('');
  const load = () => jx(`${apiBase}/api/tracker-sync/history?limit=150`).then((r) => setRows(r.body.history || []));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const revert = (h: any) => {
    if (!canEdit) return;
    setMsg('Reverting…');
    jx(`${apiBase}/api/tracker-sync/revert`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user_key: h.user_key, column_key: h.column_key, to_value: h.old_value }) })
      .then((r) => { setMsg(r.ok ? `Reverted ${h.column_key}` : 'Error: ' + (r.body.error || r.status)); load(); });
  };
  const shown = rows.filter((h) => !q || (String(h.editor_email) + h.column_key + h.user_key).toLowerCase().includes(q.toLowerCase()));
  const color = (a: string) => a === 'reject' ? 'text-red-500' : a === 'revert' ? 'text-amber-600' : a === 'source_sync' ? 'text-blue-500' : 'text-green-600';
  return (
    <Card title="Audit history & revert">
      <div className="flex gap-2 mb-2">
        <input className="input-sm flex-1" placeholder="filter by user / column / editor…" value={q} onChange={(e) => setQ(e.target.value)} />
        <button className="btn-secondary" onClick={load}>Refresh</button>
      </div>
      <div className="max-h-72 overflow-auto text-[11px]">
        {shown.length === 0 && <div className="text-gray-400">No changes recorded yet.</div>}
        {shown.map((h, i) => (
          <div key={i} className="flex items-center justify-between gap-2 py-1 border-b border-tnh-border/30 dark:border-tnh-border-dark/30">
            <div className="min-w-0">
              <span className={color(h.action)}>{h.action}</span>{' '}
              <span className="font-mono">{h.column_key}</span>: <span className="text-gray-400 line-through">{String(h.old_value || '∅').slice(0, 16)}</span> → {String(h.new_value || '∅').slice(0, 16)}
              <div className="text-gray-400 truncate">{h.user_key} · {h.editor_email} · {fmt(h.ts)}</div>
            </div>
            {canEdit && h.action === 'edit' && <button className="text-tnh-accent hover:underline shrink-0" onClick={() => revert(h)} title="restore the value before this change">revert</button>}
          </div>
        ))}
      </div>
      {msg && <div className="text-xs text-gray-500 mt-1">{msg}</div>}
    </Card>
  );
}

function Snapshots({ apiBase, canEdit }: { apiBase: string; canEdit: boolean }) {
  const [list, setList] = useState<any[]>([]);
  const [msg, setMsg] = useState('');
  const load = () => jx(`${apiBase}/api/tracker-sync/snapshots`).then((r) => setList(r.body.snapshots || []));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const snap = () => {
    setMsg('Creating…');
    jx(`${apiBase}/api/tracker-sync/snapshot`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ label: 'manual · ' + new Date().toLocaleString() }) })
      .then((r) => { setMsg(r.ok ? `Snapshot saved (${r.body.row_count} cells)` : 'Error'); load(); });
  };
  const restore = (s: any) => {
    if (!canEdit) return;
    if (!confirm(`Restore the tracker to "${s.label}"?\nCurrent values are overwritten — but every change is audited, so this is reversible.`)) return;
    setMsg('Restoring…');
    jx(`${apiBase}/api/tracker-sync/snapshot/restore`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ snapshot_id: s.snapshot_id }) })
      .then((r) => setMsg(r.ok ? `Restored ${r.body.restored} cell(s) from "${s.label}" — pulls into the sheet on next Sync` : 'Error: ' + (r.body.error || r.status)));
  };
  return (
    <Card title="Backups (snapshots)">
      <p className="text-xs text-gray-500 mb-2">Point-in-time snapshots of the live data, kept in TriNetHub. <b>Restore</b> rolls the tracker back to any version (reversible). For a full workbook file backup to re-upload to OneDrive, use <b>Export to Excel</b>.</p>
      {canEdit && <button className="btn-secondary mb-2" onClick={snap}>Create snapshot now</button>}
      <div className="max-h-44 overflow-auto text-[11px]">
        {list.length === 0 && <div className="text-gray-400">No snapshots yet — create one above.</div>}
        {list.map((s) => (
          <div key={s.snapshot_id} className="flex items-center justify-between gap-2 py-0.5 border-b border-tnh-border/30 dark:border-tnh-border-dark/30">
            <span className="min-w-0 truncate"><span className="font-mono">{s.label}</span> · {s.row_count} cells · {fmt(s.created_at)} · {s.created_by}</span>
            {canEdit && <button className="text-tnh-accent hover:underline shrink-0" onClick={() => restore(s)} title="roll the tracker back to this version">restore</button>}
          </div>
        ))}
      </div>
      {msg && <div className="text-xs text-gray-500 mt-1">{msg}</div>}
    </Card>
  );
}

export default function TrackerHistoryPanel({ me, apiBase }: { me: Me; apiBase: string }) {
  const canEdit = me.role === 'super_admin' || me.role === 'tenant_admin' || me.role === 'tenant_user_rw';
  return (
    <div className="grid md:grid-cols-2 gap-3">
      <HistoryRevert apiBase={apiBase} canEdit={canEdit} />
      <Snapshots apiBase={apiBase} canEdit={canEdit} />
    </div>
  );
}
